import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/DataTableContainer"
import DashboardLoader from "components/Loader/DashboardLoader"
import DeleteModal from "pages/Calendar/DeleteModal"
import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { selectUser } from "store/features/auth/authSlice"
import {
  useGetAllProjectsQuery,
  useDeleteProjectMutation,
  useGetClientByUserIdQuery,
} from "store/features/client/clientManagementApi"
import { useGetAllClientsQuery } from "store/features/client/clientManagementApi"
import { useGetAllDevelopersQuery } from "store/features/developer/developerManagementApi"
import ProjectWorkLogModal from "components/Modals/ProjectWorkLogModal"

const AllProjects = () => {
  const user = useSelector(selectUser)

  const { data: client, isLoading: isLoadingTwo } = useGetClientByUserIdQuery(
    user.id
  )

  const { data: projects, isLoading } = useGetAllProjectsQuery()
  const { data: clients = [] } = useGetAllClientsQuery()
  const { data: developers = [] } = useGetAllDevelopersQuery()
  const [deleteProject, { isLoading: deletingProject }] =
    useDeleteProjectMutation()

  const [deleteModal, setDeleteModal] = useState(false)
  const [workLogModal, setWorkLogModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [selectedProjectId, setSelectedProjectId] = useState(null)
  const [statusFilter, setStatusFilter] = useState("")
  const [priorityFilter, setPriorityFilter] = useState("")

  // Add this function for work log modal
  const showWorkLogModal = project => {
    setSelectedProject(project)
    setWorkLogModal(true)
  }

  const showDeleteModal = id => {
    setSelectedProjectId(id)
    setDeleteModal(true)
  }

  const handleDeleteProject = () => {
    deleteProject(selectedProjectId)
    setDeleteModal(false)
  }

  // Add this function for work log callback
  const handleWorkLogAdded = () => {
    // You can refetch projects or show a message if needed
    // refetch()
  }

  // Get client name by ID
  const getClientName = clientId => {
    const client = clients.find(client => client.id === clientId)
    return client ? client.company_name : "N/A"
  }

  // Get assigned team names
  const getTeamNames = teamIds => {
    if (!teamIds || !teamIds.length) return "No team assigned"
    const teamMembers = developers.filter(dev => teamIds.includes(dev.id))
    return teamMembers.map(dev => dev.nickname).join(", ")
  }

  // Get priority badge
  const getPriorityBadge = priority => {
    const priorityConfig = {
      1: { color: "danger", text: "High" },
      2: { color: "warning", text: "Medium" },
      3: { color: "success", text: "Low" },
      "": { color: "secondary", text: "Not Set" },
    }
    const config = priorityConfig[priority] || priorityConfig[""]
    return `<span class="badge bg-${config.color}">${config.text}</span>`
  }

  // Get status badge
  const getStatusBadge = status => {
    const statusConfig = {
      Pending: "warning",
      "In Progress": "primary",
      Completed: "success",
      "On Hold": "danger",
    }
    const color = statusConfig[status] || "secondary"
    return `<span class="badge bg-${color}">${status}</span>`
  }

  // Filter projects based on selected filters
  // const filteredProjects = useMemo(() => {
  //   if (!projects) return []

  //   return projects.filter(project => {
  //     const statusMatch = !statusFilter || project.status === statusFilter
  //     const priorityMatch =
  //       !priorityFilter || project.priority_status === priorityFilter
  //     return statusMatch && priorityMatch
  //   })
  // }, [projects, statusFilter, priorityFilter])

  // Filter projects based on selected filters and user role
  const filteredProjects = useMemo(() => {
    if (!projects) return []

    // Step 1: apply your existing status/priority filter
    let visibleProjects = projects.filter(project => {
      const statusMatch = !statusFilter || project.status === statusFilter
      const priorityMatch =
        !priorityFilter || project.priority_status === priorityFilter
      return statusMatch && priorityMatch
    })

    // Step 2: apply role-based filtering
    if (!user) return visibleProjects

    const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_")

    if (cleanRole === "super_admin" || cleanRole === "admin" || cleanRole === "marketing") {
      return visibleProjects
    }

    const isUserAssigned = (assignedList, userId) => {
      if (!assignedList) return false
      const list = Array.isArray(assignedList) ? assignedList : [assignedList]
      return list.some(idOrUser => {
        if (typeof idOrUser === "object" && idOrUser !== null) {
          return String(idOrUser.id) === String(userId)
        }
        return String(idOrUser) === String(userId)
      })
    }

    if (cleanRole === "project_manager") {
      return visibleProjects.filter(project =>
        isUserAssigned(project.assigned_project_manager, user.id) ||
        isUserAssigned(project.assigned_team, user.id)
      )
    }

    if (cleanRole === "developer") {
      return visibleProjects.filter(
        project =>
          isUserAssigned(project.assigned_frontend_dev, user.id) ||
          isUserAssigned(project.assigned_backend_dev, user.id) ||
          isUserAssigned(project.assigned_team, user.id)
      )
    }

    if (cleanRole === "designer") {
      return visibleProjects.filter(
        project =>
          isUserAssigned(project.assigned_designer, user.id) ||
          isUserAssigned(project.assigned_team, user.id)
      )
    }

    if (cleanRole === "client") {
      return visibleProjects.filter(project => String(project.client) === String(user.id))
    }

    // Default for other team members: show if assigned to any role in project
    return visibleProjects.filter(
      project =>
        isUserAssigned(project.assigned_frontend_dev, user.id) ||
        isUserAssigned(project.assigned_backend_dev, user.id) ||
        isUserAssigned(project.assigned_designer, user.id) ||
        isUserAssigned(project.assigned_team, user.id) ||
        isUserAssigned(project.assigned_project_manager, user.id)
    )
  }, [projects, statusFilter, priorityFilter, user])

  // const columns = useMemo(
  //   () => [
  //     {
  //       header: "Project Name",
  //       accessorKey: "name",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => (
  //         <div>
  //           <div className="fw-semibold">{row.original.name}</div>
  //           <small className="text-muted">{row.original.description}</small>
  //         </div>
  //       ),
  //     },
  //     {
  //       header: "Client",
  //       accessorKey: "client",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => getClientName(row.original.client),
  //     },
  //     {
  //       header: "Budget",
  //       accessorKey: "budget",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => `$${row.original.budget || "0.00"}`,
  //     },
  //     // {
  //     //   header: "Team",
  //     //   accessorKey: "assigned_team",
  //     //   enableColumnFilter: false,
  //     //   enableSorting: true,
  //     //   cell: ({ row }) => (
  //     //     <div>
  //     //       <small>{getTeamNames(row.original.assigned_team)}</small>
  //     //     </div>
  //     //   ),
  //     // },
  //     {
  //       header: "Start Date",
  //       accessorKey: "start_date",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => row.original.start_date || "Not set",
  //     },
  //     {
  //       header: "End Date",
  //       accessorKey: "end_date",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => row.original.end_date || "Not set",
  //     },
  //     // {
  //     //   header: "Status",
  //     //   accessorKey: "status",
  //     //   enableColumnFilter: false,
  //     //   enableSorting: true,
  //     //   cell: ({ row }) => (
  //     //     <div
  //     //       dangerouslySetInnerHTML={{
  //     //         __html: getStatusBadge(row.original.status),
  //     //       }}
  //     //     />
  //     //   ),
  //     // },
  //     // {
  //     //   header: "Transactions",
  //     //   accessorKey: "payment",
  //     //   enableColumnFilter: false,
  //     //   enableSorting: true,
  //     //   cell: ({ row }) => {
  //     //     const payments = row.original.payment || []
  //     //     if (payments.length === 0) return "No payments"

  //     //     return (
  //     //       <div>
  //     //         {payments.map((payment, index) => (
  //     //           <div key={payment.id}>
  //     //             <small className="fs-6">
  //     //               Payment {index + 1}:{" "}
  //     //               <span className="fw-semibold">
  //     //                 {payment.amount || "0.00"}
  //     //               </span>
  //     //             </small>
  //     //           </div>
  //     //         ))}
  //     //       </div>
  //     //     )
  //     //   },
  //     // },
  //     {
  //       header: "Transactions",
  //       accessorKey: "payment",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => {
  //         const payments = row.original.payment || []
  //         const budget = parseFloat(row.original.budget || 0)
  //         const totalPaid = payments.reduce(
  //           (sum, payment) => sum + parseFloat(payment.amount || 0),
  //           0
  //         )
  //         const remaining = budget - totalPaid

  //         if (payments.length === 0) {
  //           return (
  //             <div className="fs-5">
  //               <small>No payments</small>
  //               <br />
  //               <small className="text-muted">
  //                 Budget: {budget.toFixed(0)}
  //               </small>
  //             </div>
  //           )
  //         }

  //         return (
  //           <div className="text-start fs-5">
  //             {payments.map((payment, index) => (
  //               <div
  //                 key={payment.id}
  //                 className="d-flex justify-content-between align-items-center"
  //               >
  //                 <small>Payment {index + 1}</small>
  //                 <small className="fw-semibold">
  //                   {parseFloat(payment.amount).toFixed(0)}
  //                 </small>
  //               </div>
  //             ))}

  //             <hr className="my-1" />

  //             <div className="d-flex justify-content-between align-items-center">
  //               <small className="fw-semibold text-success">Total Paid</small>
  //               <small className="fw-semibold text-success">
  //                 {totalPaid.toFixed(0)}
  //               </small>
  //             </div>

  //             <div className="d-flex justify-content-between align-items-center">
  //               <small className="fw-semibold text-danger">Remaining</small>
  //               <small className="fw-semibold text-danger">
  //                 {remaining.toFixed(0)}
  //               </small>
  //             </div>
  //           </div>
  //         )
  //       },
  //     },

  //     {
  //       header: "Priority",
  //       accessorKey: "priority_status",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => (
  //         <div
  //           dangerouslySetInnerHTML={{
  //             __html: getPriorityBadge(row.original.priority_status),
  //           }}
  //         />
  //       ),
  //     },
  //     {
  //       header: "Progress",
  //       accessorKey: "progress",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => (
  //         <div>
  //           <div className="progress mb-1" style={{ height: "6px" }}>
  //             <div
  //               className="progress-bar"
  //               role="progressbar"
  //               style={{ width: `${row.original.progress || 0}%` }}
  //               aria-valuenow={row.original.progress || 0}
  //               aria-valuemin="0"
  //               aria-valuemax="100"
  //             />
  //           </div>
  //           <small>{row.original.progress || 0}%</small>
  //         </div>
  //       ),
  //     },

  //     {
  //       header: "Action",
  //       accessorKey: "action",
  //       enableColumnFilter: false,
  //       enableSorting: true,
  //       cell: ({ row }) => (
  //         <div className="d-flex align-items-center gap-1">
  //           <Link
  //             to={`/projects/${row.original.id}`}
  //             className="btn btn-info btn-sm"
  //           >
  //             <i className="fas fa-eye"></i> View
  //           </Link>

  //           {(user?.role == "super_admin" || user?.role == "admin") && (
  //             <button
  //               className="btn btn-danger btn-sm"
  //               onClick={() => showDeleteModal(row.original.id)}
  //             >
  //               <i className="fas fa-trash"></i>
  //               {deletingProject ? "Deleting..." : "Delete"}
  //             </button>
  //           )}
  //         </div>
  //       ),
  //     },
  //   ],
  //   [clients, developers, deletingProject]
  // )

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "Project Name",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.name}</div>
            {/* <small className="text-muted">{row.original.description}</small> */}
          </div>
        ),
      },
    ]

    // Add admin-only columns
    if (user?.role === "super_admin" || user?.role === "admin") {
      baseColumns.push(
        {
          header: "Client",
          accessorKey: "client",
          enableColumnFilter: false,
          enableSorting: true,
          cell: ({ row }) => getClientName(row.original.client),
        },
        {
          header: "Budget",
          accessorKey: "budget",
          enableColumnFilter: false,
          enableSorting: true,
          cell: ({ row }) => `${row.original.budget || "0.00"}`,
        }
      )
    }

    // Add common columns for all users
    // baseColumns.push(
    //   {
    //     header: "Start Date",
    //     accessorKey: "start_date",
    //     enableColumnFilter: false,
    //     enableSorting: true,
    //     cell: ({ row }) => row.original.start_date || "Not set",
    //   },
    //   {
    //     header: "End Date",
    //     accessorKey: "end_date",
    //     enableColumnFilter: false,
    //     enableSorting: true,
    //     cell: ({ row }) => row.original.end_date || "Not set",
    //   }
    // )

    // Add admin-only columns
    if (user?.role === "super_admin" || user?.role === "admin") {
      baseColumns.push({
        header: "Transactions",
        accessorKey: "payment",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const payments = row.original.payment || []
          const budget = parseFloat(row.original.budget || 0)
          const totalPaid = payments.reduce(
            (sum, payment) => sum + parseFloat(payment.amount || 0),
            0
          )
          const remaining = budget - totalPaid

          if (payments.length === 0) {
            return (
              <div className="fs-5">
                <small>No payments</small>
                <br />
                <small className="text-muted">
                  Budget: {budget.toFixed(0)}
                </small>
              </div>
            )
          }

          return (
            <div className="text-start">
              {payments.map((payment, index) => (
                <div
                  key={payment.id}
                  className="d-flex justify-content-between mb-1"
                >
                  <div className="">
                    <small className="fs-6">Payment {index + 1}:</small>
                    <br />
                    <small className="">{payment?.date}</small>
                  </div>
                  <small className="fw-semibold fs-6">
                    {Number(
                      parseFloat(payment.amount).toFixed(0)
                    ).toLocaleString()}
                  </small>
                </div>
              ))}

              <hr className="my-1" />

              <div className="d-flex justify-content-between align-items-center">
                <small className="fw-semibold text-success">Total Paid</small>
                <small className="fw-semibold text-success">
                  {Number(totalPaid.toFixed(0)).toLocaleString()}
                </small>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <small className="fw-semibold text-danger">Remaining</small>
                <small className="fw-semibold text-danger">
                  {Number(remaining.toFixed(0)).toLocaleString()}
                </small>
              </div>
            </div>
          )
        },
      })
    }

    baseColumns.push(
      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => <div className="">{row.original.status}</div>,
      // },
      // {
      //   header: "Priority",
      //   accessorKey: "priority_status",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div
      //       dangerouslySetInnerHTML={{
      //         __html: getPriorityBadge(row.original.priority_status),
      //       }}
      //     />
      //   ),
      // },
      {
        header: "Progress",
        accessorKey: "progress",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="progress mb-1" style={{ height: "6px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${row.original.progress || 0}%` }}
                aria-valuenow={row.original.progress || 0}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
            <small>{row.original.progress || 0}%</small>
          </div>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            <div className="d-flex align-items-center gap-1">
              <Link
                to={`/projects/${row.original.id}`}
                className="btn btn-info btn-sm"
              >
                <i className="fas fa-eye"></i>
              </Link>

              <button
                className="btn btn-success btn-sm"
                onClick={() => showWorkLogModal(row.original)}
                title="Add Work Log for this project"
              >
                <i className="fas fa-plus"></i>
              </button>

              {(user?.role == "super_admin" || user?.role == "admin") && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => showDeleteModal(row.original.id)}
                >
                  <i className="fas fa-trash"></i>
                  {/* {deletingProject ? "Deleting..." : "Delete"} */}
                </button>
              )}
            </div>
            <div className="d-flex align-items-center gap-1">
              {(user?.role == "super_admin" || user?.role == "admin") && (
                <Link
                  to={`/all-projects/invoice/${row.original.id}`}
                  className="btn btn-warning btn-sm mt-2"
                  title="View Invoice"
                >
                  <i className="fas fa-file-invoice"></i>
                </Link>
              )}
              {(user?.role == "super_admin" || user?.role == "admin") && (
                <Link
                  to={`/all-projects/money-receipt/${row.original.id}`}
                  className="btn btn-info btn-sm mt-2"
                  title="View Recept"
                >
                  <i className="fas fa-file-invoice"></i>
                </Link>
              )}
            </div>
          </div>
        ),
      }
    )

    return baseColumns
  }, [clients, developers, deletingProject, user])

  const handleExportPDF = () => {
    if (!filteredProjects || filteredProjects.length === 0) {
      toast.warning("No projects available to export PDF")
      return
    }

    const priorityMap = { 1: "High", 2: "Medium", 3: "Low" }
    const activeFilters = statusFilter ? `Status: ${statusFilter}` : "All Projects (No Filters)"

    let totalBudgetSum = 0
    let totalPaidSum = 0
    let totalDueSum = 0

    const rowsHtml = filteredProjects
      .map((project, idx) => {
        const clientName = getClientName(project.client)
        const budget = parseFloat(project.budget || 0)
        const payments = Array.isArray(project.payment) ? project.payment : []
        const paid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        const due = Math.max(0, budget - paid)
        const priorityText = priorityMap[project.priority_status] || "Not Set"
        const startDate = project.start_date ? new Date(project.start_date).toISOString().split("T")[0] : "-"
        const endDate = project.end_date ? new Date(project.end_date).toISOString().split("T")[0] : "-"

        totalBudgetSum += budget
        totalPaidSum += paid
        totalDueSum += due

        return `
          <tr>
            <td style="text-align: center;">${idx + 1}</td>
            <td style="font-weight: 600;">${project.name || "-"}</td>
            <td>${clientName}</td>
            <td style="text-align: right; font-weight: 600;">৳ ${budget.toLocaleString()}</td>
            <td style="text-align: right; color: #28a745; font-weight: 600;">৳ ${paid.toLocaleString()}</td>
            <td style="text-align: right; color: #dc3545; font-weight: 600;">৳ ${due.toLocaleString()}</td>
            <td style="text-align: center;">${project.status || "Pending"}</td>
            <td style="text-align: center;">${priorityText}</td>
            <td style="text-align: center;">${startDate}</td>
            <td style="text-align: center;">${endDate}</td>
          </tr>
        `
      })
      .join("")

    const printWindow = window.open("", "_blank")
    if (!printWindow) {
      toast.error("Please allow popups to download/print PDF")
      return
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Projects Report - Softzen IT</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; margin: 0; padding: 20px; background: #fff; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #2a3042; padding-bottom: 12px; margin-bottom: 15px; }
            .logo { font-size: 24px; font-weight: bold; color: #556ee6; }
            .title { font-size: 18px; font-weight: bold; color: #2a3042; text-align: right; }
            .summary-cards { display: flex; gap: 15px; margin-bottom: 15px; }
            .card { flex: 1; background: #f8f9fa; padding: 10px 15px; border-radius: 6px; border: 1px solid #e9ecef; }
            .card-title { font-size: 11px; text-transform: uppercase; color: #74788d; font-weight: 600; }
            .card-value { font-size: 16px; font-weight: bold; color: #2a3042; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th { background-color: #2a3042; color: #ffffff; text-align: left; padding: 8px 10px; font-weight: 600; text-transform: uppercase; font-size: 10px; }
            td { padding: 8px 10px; border-bottom: 1px solid #e9ecef; color: #495057; }
            tr:nth-child(even) { background-color: #f8f9fa; }
            .footer-row td { font-weight: bold; background-color: #e9ecef !important; border-top: 2px solid #2a3042; }
            .footer { margin-top: 20px; text-align: right; font-size: 11px; color: #74788d; border-top: 1px solid #e9ecef; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Softzen IT</div>
            <div class="title">Projects Summary Report</div>
          </div>

          <div class="summary-cards">
            <div class="card">
              <div class="card-title">Total Projects</div>
              <div class="card-value">${filteredProjects.length}</div>
            </div>
            <div class="card">
              <div class="card-title">Total Budget</div>
              <div class="card-value">৳ ${totalBudgetSum.toLocaleString()}</div>
            </div>
            <div class="card">
              <div class="card-title">Total Paid</div>
              <div class="card-value" style="color: #28a745;">৳ ${totalPaidSum.toLocaleString()}</div>
            </div>
            <div class="card">
              <div class="card-title">Total Remaining Due</div>
              <div class="card-value" style="color: #dc3545;">৳ ${totalDueSum.toLocaleString()}</div>
            </div>
          </div>

          <div style="font-size: 12px; color: #666; margin-bottom: 10px;">
            <strong>Filter Applied:</strong> ${activeFilters} | <strong>Generated On:</strong> ${new Date().toLocaleString()}
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 30px; text-align: center;">SL</th>
                <th>Project Name</th>
                <th>Client</th>
                <th style="text-align: right;">Budget</th>
                <th style="text-align: right;">Total Paid</th>
                <th style="text-align: right;">Due</th>
                <th style="text-align: center;">Status</th>
                <th style="text-align: center;">Priority</th>
                <th style="text-align: center;">Start Date</th>
                <th style="text-align: center;">End Date</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
              <tr class="footer-row">
                <td colspan="3" style="text-align: right;">Total Summary:</td>
                <td style="text-align: right;">৳ ${totalBudgetSum.toLocaleString()}</td>
                <td style="text-align: right; color: #28a745;">৳ ${totalPaidSum.toLocaleString()}</td>
                <td style="text-align: right; color: #dc3545;">৳ ${totalDueSum.toLocaleString()}</td>
                <td colspan="4"></td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            Report generated by Softzen IT Management System
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `

    printWindow.document.open()
    printWindow.document.write(htmlContent)
    printWindow.document.close()
  }

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">

          {/* Breadcrumb Section */}
          <div>
            <Breadcrumb title="Projects" breadcrumbItem="Projects" />
          </div>

          {/* Action Buttons */}
          <div className="d-flex flex-wrap gap-2">

            <button
              type="button"
              onClick={handleExportPDF}
              className="btn btn-danger d-flex align-items-center px-3"
              title="Download PDF report of filtered projects"
            >
              <i className="fas fa-file-pdf me-2"></i>
              Export PDF ({filteredProjects?.length || 0})
            </button>

            <Link
              to="/worklog"
              className="btn btn-primary d-flex align-items-center px-3"
            >
              <i className="fas fa-plus me-2"></i>
              Work Log
            </Link>

          </div>
        </div>

        {/* Filters Section */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Filter by Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
            </select>
          </div>
          {/* <div className="col-md-4">
            <label className="form-label">Filter by Priority</label>
            <select
              className="form-select"
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
            >
              <option value="">All Priority</option>
              <option value="1">High</option>
              <option value="2">Medium</option>
              <option value="3">Low</option>
            </select>
          </div> */}
          <div className="col-md-4 d-flex align-items-end">
            <button
              className="btn btn-light"
              onClick={() => {
                setStatusFilter("")
                setPriorityFilter("")
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filteredProjects}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search Projects..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteProject}
          loader={deletingProject}
          onCloseClick={() => setDeleteModal(false)}
        />

        {/* Work Log Modal */}
        <ProjectWorkLogModal
          isOpen={workLogModal}
          toggle={() => setWorkLogModal(false)}
          project={selectedProject}
          onWorkLogAdded={handleWorkLogAdded}
        />
      </div>
    </div>
  )
}

export default AllProjects
